import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FileText, 
  Upload, 
  Sparkles, 
  RefreshCw,
  Eye,
  Settings,
  CheckCircle,
  Clock,
  FileUp,
  Save,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const EBAPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [interpreting, setInterpreting] = useState(null);
  const [generatingRules, setGeneratingRules] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Ordinary hours configuration state
  const [hoursConfigDialogOpen, setHoursConfigDialogOpen] = useState(false);
  const [selectedEbaForConfig, setSelectedEbaForConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [hoursConfig, setHoursConfig] = useState({
    weekly_hours: 36,
    daily_hours: 8,
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    start_time: '06:00',
    end_time: '18:00',
    overtime_triggers: {
      outside_working_hours: true,
      excess_daily_hours: true,
      excess_weekly_hours: true,
      weekends: true,
      public_holidays: true
    }
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteRulesAlso, setDeleteRulesAlso] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDocuments = async () => {
    try {
      const response = await API.get('/eba');
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch EBA documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Resume polling for any document that is mid-interpretation or mid-generation (e.g. after reload)
  useEffect(() => {
    if (!documents || documents.length === 0) return;
    const interpretingDoc = documents.find((d) => d.status === 'interpreting');
    if (interpretingDoc && interpreting !== interpretingDoc.id) {
      setInterpreting(interpretingDoc.id);
      pollInterpretation(interpretingDoc.id);
    }
    const generatingDoc = documents.find((d) => d.generation_status === 'generating');
    if (generatingDoc && generatingRules !== generatingDoc.id) {
      setGeneratingRules(generatingDoc.id);
      pollGeneration(generatingDoc.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await API.post('/eba/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Document uploaded: ${response.data.filename}`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });

  const handleInterpret = async (docId) => {
    setInterpreting(docId);
    try {
      await API.post(`/eba/${docId}/interpret`);
      toast.info('Interpretation started — reading the full agreement. This can take a few minutes for large EBAs.');
      pollInterpretation(docId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Interpretation failed to start');
      setInterpreting(null);
    }
  };

  const pollInterpretation = (docId) => {
    const started = Date.now();
    const timer = setInterval(async () => {
      try {
        const res = await API.get(`/eba/${docId}`);
        const status = res.data?.status;
        // Keep the list in sync so the status badge updates live
        setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, ...res.data } : d)));
        if (status === 'interpreted') {
          clearInterval(timer);
          setInterpreting(null);
          toast.success(`EBA interpreted — ${res.data?.rules_extracted ?? ''} payroll rules extracted. Review them, then Generate Rules.`);
          fetchDocuments();
        } else if (status === 'interpret_failed') {
          clearInterval(timer);
          setInterpreting(null);
          toast.error(res.data?.interpret_error || 'Interpretation failed');
          fetchDocuments();
        } else if (Date.now() - started > 10 * 60 * 1000) {
          // Safety stop after 10 minutes
          clearInterval(timer);
          setInterpreting(null);
          toast.error('Interpretation is taking longer than expected. Please refresh shortly.');
        }
      } catch (error) {
        // Transient error — keep polling
      }
    }, 6000);
  };

  const handleGenerateRules = async (docId) => {
    setGeneratingRules(docId);
    try {
      await API.post(`/rules/generate-from-eba/${docId}`);
      toast.info('Generating payroll rules from the interpretation…');
      pollGeneration(docId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rule generation');
      setGeneratingRules(null);
    }
  };

  const pollGeneration = (docId) => {
    const started = Date.now();
    const timer = setInterval(async () => {
      try {
        const res = await API.get(`/eba/${docId}`);
        const status = res.data?.generation_status;
        setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, ...res.data } : d)));
        if (status === 'generated') {
          clearInterval(timer);
          setGeneratingRules(null);
          toast.success(`Generated ${res.data?.rules_generated ?? ''} payroll rules. Review and enable the ones that apply in the Rules Engine.`);
        } else if (status === 'generate_failed') {
          clearInterval(timer);
          setGeneratingRules(null);
          toast.error(res.data?.generation_error || 'Rule generation failed');
        } else if (Date.now() - started > 5 * 60 * 1000) {
          clearInterval(timer);
          setGeneratingRules(null);
          toast.error('Rule generation is taking longer than expected. Please refresh shortly.');
        }
      } catch (error) {
        // transient — keep polling
      }
    }, 4000);
  };

  const handleViewDetails = async (docId) => {
    try {
      const response = await API.get(`/eba/${docId}`);
      setSelectedDoc(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch document details');
    }
  };

  const handleOpenHoursConfig = async (doc) => {
    try {
      const response = await API.get(`/eba/${doc.id}/ordinary-hours`);
      setSelectedEbaForConfig(doc);
      setHoursConfig(response.data.config);
      setHoursConfigDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load hours configuration');
    }
  };

  const handleSaveHoursConfig = async () => {
    if (!selectedEbaForConfig) return;
    
    setSavingConfig(true);
    try {
      await API.put(`/eba/${selectedEbaForConfig.id}/ordinary-hours`, hoursConfig);
      toast.success('Ordinary hours configuration saved');
      setHoursConfigDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setHoursConfig(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  };

  const updateOvertimeTrigger = (key, value) => {
    setHoursConfig(prev => ({
      ...prev,
      overtime_triggers: {
        ...prev.overtime_triggers,
        [key]: value
      }
    }));
  };

  // Delete document handlers
  const openDeleteDialog = (doc) => {
    setDocumentToDelete(doc);
    setDeleteRulesAlso(false);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    setDeleting(true);
    try {
      const response = await API.delete(`/eba/${documentToDelete.id}?delete_rules=${deleteRulesAlso}`);
      toast.success(response.data.message);
      if (response.data.rules_deleted > 0) {
        toast.info(`${response.data.rules_deleted} associated rules were also deleted`);
      }
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">EBA Documents</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Upload and interpret Enterprise Bargaining Agreements</p>
        </div>
      </div>

      {/* Upload Zone */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
              isDragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
            }`}
            data-testid="eba-upload-dropzone"
          >
            <input {...getInputProps()} data-testid="eba-file-input" />
            <div className="flex flex-col items-center gap-3">
              {uploading ? (
                <RefreshCw className="w-10 h-10 text-blue-500 animate-spin" />
              ) : (
                <FileUp className="w-10 h-10 text-slate-400" />
              )}
              <div>
                <p className="text-sm font-medium text-slate-700">
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop an EBA document'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Supports PDF and DOCX files
                </p>
              </div>
              <Button variant="outline" size="sm" className="mt-2" disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />
                Browse Files
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card className="border-slate-200 animate-fade-in-up-delay-2">
        <CardHeader>
          <CardTitle className="text-lg font-heading font-semibold text-primary">
            Uploaded Documents
          </CardTitle>
          <CardDescription>
            {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No documents uploaded yet</p>
              <p className="text-sm text-slate-400">Upload an EBA document to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} data-testid={`eba-row-${doc.id}`}>
                      <TableCell className="font-medium">{doc.filename}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="uppercase text-xs">
                          {doc.file_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${
                          doc.status === 'interpreted' 
                            ? 'status-badge-success' 
                            : 'status-badge-warning'
                        }`}>
                          {doc.status === 'interpreted' ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : (
                            <Clock className="w-3 h-3 mr-1" />
                          )}
                          {doc.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {formatDate(doc.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {doc.status !== 'interpreted' && (
                            <Button
                              size="sm"
                              onClick={() => handleInterpret(doc.id)}
                              disabled={interpreting === doc.id || doc.status === 'interpreting'}
                              className="gap-1"
                              data-testid={`interpret-btn-${doc.id}`}
                            >
                              {(interpreting === doc.id || doc.status === 'interpreting') ? (
                                <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Interpreting...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  {doc.status === 'interpret_failed' ? 'Retry' : 'Interpret'}
                                </>
                              )}
                            </Button>
                          )}
                          {doc.status === 'interpreted' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGenerateRules(doc.id)}
                              className="gap-1"
                              disabled={generatingRules === doc.id || doc.generation_status === 'generating'}
                              data-testid={`generate-rules-btn-${doc.id}`}
                            >
                              {(generatingRules === doc.id || doc.generation_status === 'generating') ? (
                                <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Settings className="w-3 h-3" />
                                  Generate Rules
                                </>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenHoursConfig(doc)}
                            title="Configure Ordinary Hours"
                            data-testid={`config-hours-btn-${doc.id}`}
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(doc.id)}
                            data-testid={`view-btn-${doc.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDeleteDialog(doc)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Delete Document"
                            data-testid={`delete-btn-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">{selectedDoc?.filename}</DialogTitle>
            <DialogDescription>
              EBA document details and interpretation results
            </DialogDescription>
          </DialogHeader>
          
          {selectedDoc && (
            <ScrollArea className="h-[60vh] pr-4">
              {selectedDoc.interpretation && selectedDoc.interpretation.length > 0 ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                      Interpreted Pay Rates
                    </h3>
                    <Badge variant="secondary">
                      {selectedDoc.interpretation.length} Sections
                    </Badge>
                  </div>
                  
                  {/* Check if hierarchical format (has section_name) or flat format */}
                  {selectedDoc.interpretation[0]?.section_name ? (
                    // Hierarchical format
                    <div className="space-y-6">
                      {selectedDoc.interpretation.map((section, sectionIdx) => (
                        <div 
                          key={sectionIdx}
                          className="border border-slate-200 rounded-lg overflow-hidden"
                        >
                          {/* Section Header */}
                          <div className="bg-equipr-yellow/10 border-b border-slate-200 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="w-7 h-7 rounded-full bg-equipr-black text-equipr-yellow text-sm font-bold flex items-center justify-center">
                                {section.section_number || sectionIdx + 1}
                              </span>
                              <h4 className="font-heading font-semibold text-equipr-black text-lg">
                                {section.section_name}
                              </h4>
                            </div>
                            {section.description && (
                              <p className="text-sm text-slate-600 ml-9">
                                {section.description}
                              </p>
                            )}
                          </div>
                          
                          {/* Rates Table */}
                          {section.rates && section.rates.length > 0 && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 border-b border-slate-200">
                                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Category</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Rate Type</th>
                                    <th className="text-right px-4 py-2 font-semibold text-slate-600">Value</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Unit</th>
                                    <th className="text-left px-4 py-2 font-semibold text-slate-600">Conditions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {section.rates.map((rate, rateIdx) => (
                                    <tr key={rateIdx} className="border-b border-slate-100 hover:bg-slate-50">
                                      <td className="px-4 py-2.5 font-medium text-slate-800">{rate.category}</td>
                                      <td className="px-4 py-2.5 text-slate-600">{rate.rate_type}</td>
                                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-primary">
                                        {typeof rate.value === 'number' ? rate.value.toLocaleString() : rate.value}
                                      </td>
                                      <td className="px-4 py-2.5 text-slate-600">{rate.unit}</td>
                                      <td className="px-4 py-2.5 text-slate-500 text-xs max-w-xs">{rate.conditions}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Flat format (legacy)
                    <div className="space-y-3">
                      {selectedDoc.interpretation.map((item, index) => (
                        <div 
                          key={index}
                          className="p-4 bg-slate-50 rounded border border-slate-200"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="font-medium text-slate-900">{item.category}</span>
                            <Badge variant="outline">{item.rate_type}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-3">
                            <div>
                              <span className="text-xs text-slate-500">Value</span>
                              <p className="font-mono font-semibold text-primary">
                                {item.value} {item.unit}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-slate-500">Conditions</span>
                              <p className="text-sm text-slate-700">{item.conditions}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Document not yet interpreted</p>
                  <p className="text-sm text-slate-400">
                    Click &quot;Interpret&quot; to analyze this EBA
                  </p>
                </div>
              )}
              
              {selectedDoc.raw_text && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
                    Extracted Text Preview
                  </h3>
                  <div className="p-4 bg-slate-100 rounded border border-slate-200 max-h-48 overflow-auto">
                    <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                      {selectedDoc.raw_text.substring(0, 2000)}
                      {selectedDoc.raw_text.length > 2000 && '...'}
                    </pre>
                  </div>
                </div>
              )}
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Hours Configuration Dialog */}
      <Dialog open={hoursConfigDialogOpen} onOpenChange={setHoursConfigDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Ordinary Hours Configuration
            </DialogTitle>
            <DialogDescription>
              {selectedEbaForConfig?.filename} - Define what constitutes ordinary hours and overtime triggers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Weekly & Daily Hours */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Weekly Ordinary Hours</Label>
                <Input
                  type="number"
                  value={hoursConfig.weekly_hours}
                  onChange={(e) => setHoursConfig(prev => ({ ...prev, weekly_hours: parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-500">Total ordinary hours per week</p>
              </div>
              <div className="space-y-2">
                <Label>Daily Ordinary Hours</Label>
                <Input
                  type="number"
                  value={hoursConfig.daily_hours}
                  onChange={(e) => setHoursConfig(prev => ({ ...prev, daily_hours: parseFloat(e.target.value) || 0 }))}
                />
                <p className="text-xs text-slate-500">Maximum per day before OT</p>
              </div>
            </div>

            {/* Working Window */}
            <div className="space-y-2">
              <Label>Ordinary Working Hours Window</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Start Time</Label>
                  <Input
                    type="time"
                    value={hoursConfig.start_time}
                    onChange={(e) => setHoursConfig(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End Time</Label>
                  <Input
                    type="time"
                    value={hoursConfig.end_time}
                    onChange={(e) => setHoursConfig(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Work outside this window triggers overtime</p>
            </div>

            {/* Working Days */}
            <div className="space-y-2">
              <Label>Working Days (Ordinary)</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day.value}
                    className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                      hoursConfig.working_days?.includes(day.value)
                        ? 'bg-equipr-black text-equipr-yellow'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => toggleWorkingDay(day.value)}
                  >
                    {day.label.substring(0, 3)}
                  </div>
                ))}
              </div>
            </div>

            {/* Overtime Triggers */}
            <div className="space-y-3">
              <Label>Overtime Triggers</Label>
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Outside working hours window</span>
                  <Switch
                    checked={hoursConfig.overtime_triggers?.outside_working_hours ?? true}
                    onCheckedChange={(v) => updateOvertimeTrigger('outside_working_hours', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Exceeds daily hours limit</span>
                  <Switch
                    checked={hoursConfig.overtime_triggers?.excess_daily_hours ?? true}
                    onCheckedChange={(v) => updateOvertimeTrigger('excess_daily_hours', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Exceeds weekly hours limit</span>
                  <Switch
                    checked={hoursConfig.overtime_triggers?.excess_weekly_hours ?? true}
                    onCheckedChange={(v) => updateOvertimeTrigger('excess_weekly_hours', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Work on weekends</span>
                  <Switch
                    checked={hoursConfig.overtime_triggers?.weekends ?? true}
                    onCheckedChange={(v) => updateOvertimeTrigger('weekends', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Work on public holidays</span>
                  <Switch
                    checked={hoursConfig.overtime_triggers?.public_holidays ?? true}
                    onCheckedChange={(v) => updateOvertimeTrigger('public_holidays', v)}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveHoursConfig} disabled={savingConfig}>
              {savingConfig ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete EBA Document
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{documentToDelete?.filename}</strong>?
              </p>
              <p className="text-sm text-slate-500">
                This action cannot be undone.
              </p>
              
              {documentToDelete?.status === 'interpreted' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="delete-rules"
                      checked={deleteRulesAlso}
                      onCheckedChange={setDeleteRulesAlso}
                    />
                    <div>
                      <label htmlFor="delete-rules" className="text-sm font-medium text-amber-800 cursor-pointer">
                        Also delete associated payroll rules
                      </label>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Rules generated from this EBA will be permanently removed
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EBAPage;
